import { Pencil, Trash2 } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { cestinoAPI } from '../services/api';
import { messaggioDa } from '../utils/notifiche';
import '../styles/Cestino.css';

/**
 * Cestino — schermata riservata agli admin.
 *
 * Mostra le cancellazioni non ancora ripristinate e permette di
 * riportare indietro i dati. L'eliminazione definitiva richiede di
 * digitare la parola di conferma: una rotta irreversibile non deve
 * poter essere attivata per distrazione.
 *
 * LE VISITE (migration 291, proposta 25): ci arrivano da «Elimina» nella
 * scheda del paziente e dalle bozze di fattura eliminate. Si ripristinano
 * come i pazienti, e non si eliminano per sempre (risposta 4 di Alex). Se i
 * prodotti resi in magazzino non possono piu' tornare scaricati, il server
 * risponde 409 serve_conferma col perche', e si chiede.
 */

/* Il messaggio del SERVER prima di tutto (2 ottobre 2026, proposta 25): prima
   qui veniva prima quello di axios, «Request failed with status code 409», e
   il motivo vero del rifiuto non arrivava a schermo. */
const messaggioErrore = (e, ripiego) => messaggioDa(e, ripiego);

const dataOra = (v) =>
    v ? new Date(v).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const etichettaEntita = {
    paziente: 'Paziente',
    visita: 'Visita',
    visita_amministrativa: 'Visita amministrativa',
    anamnesi_voce: 'Voce di anamnesi',
    annotazione_corporea: 'Annotazione della mappa corporea',
    documento: 'Documento',
    anamnesi: 'Anamnesi',
};

/* IL FILTRO E LE PAGINE (revisione del 3/10/2026). Le visite non si
   eliminano mai e ogni bozza di fattura eliminata ne porta una
   amministrativa: con un elenco fermo alle ultime 200 le voci piu' vecchie,
   pazienti compresi, sparivano senza dirlo e non si ripristinavano piu'.
   Adesso il server manda 100 voci alla volta, dice se ce ne sono altre e
   quante per tipo; «Mostra le più vecchie» aggiunge la pagina dopo. */
const FILTRI = [
    ['', 'Tutto'],
    ['paziente', 'Pazienti'],
    ['visita', 'Visite'],
    ['visita_amministrativa', 'Visite amministrative (bozze di fattura eliminate)'],
    ['anamnesi_voce', 'Voci di anamnesi'],
    ['annotazione_corporea', 'Annotazioni della mappa corporea'],
];

const Cestino = () => {
    const [voci, setVoci] = useState([]);
    const [tipo, setTipo] = useState('');
    const [altre, setAltre] = useState(false);
    const [conteggi, setConteggi] = useState({});
    const [caricamento, setCaricamento] = useState(true);
    const [inPagina, setInPagina] = useState(false);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);
    const [inCorso, setInCorso] = useState(null);

    // Eliminazione definitiva
    const [daEliminare, setDaEliminare] = useState(null);
    const [conferma, setConferma] = useState('');

    /* `dopo`: l'ultima voce mostrata, per la pagina successiva; senza, si
       riparte dalla prima pagina del tipo scelto. */
    const carica = async (perTipo = tipo, dopo = null) => {
        // la pagina dopo non toglie la tabella mentre arriva
        const segna = dopo ? setInPagina : setCaricamento;
        try {
            segna(true);
            const r = await cestinoAPI.getAll(perTipo, dopo ? dopo.id : null);
            const d = r.data.data || {};
            setVoci((prima) => (dopo ? [...prima, ...(d.cancellazioni || [])] : (d.cancellazioni || [])));
            setAltre(!!d.altre);
            setConteggi(d.conteggi || {});
        } catch (e) {
            setErrore(messaggioErrore(e, 'Impossibile caricare il cestino'));
        } finally {
            segna(false);
        }
    };

    useEffect(() => { carica(); }, []);

    const cambiaTipo = (nuovo) => {
        setTipo(nuovo);
        carica(nuovo, null);
    };
    const totale = (chiave) => (chiave ? (conteggi[chiave] || 0)
        : Object.values(conteggi).reduce((a, n) => a + n, 0));

    const ripristina = async (v) => {
        if (!window.confirm(`Ripristinare "${v.descrizione}"?\n\nTornerà visibile con tutti i dati collegati.`)) return;
        setErrore(null);
        setAvviso(null);
        try {
            setInCorso(v.id);
            let r;
            try {
                r = await cestinoAPI.ripristina(v.id);
            } catch (e) {
                /* I prodotti di una visita che non possono tornare scaricati:
                   il server dice quali e perche', e si chiede conferma. */
                if (e?.response?.status === 409 && e?.response?.data?.serve_conferma) {
                    if (!window.confirm(messaggioErrore(e, 'Serve una conferma per il magazzino'))) return;
                    r = await cestinoAPI.ripristina(v.id, true);
                } else {
                    throw e;
                }
            }
            setAvviso(r?.data?.message && r.data.message !== 'Ripristino completato'
                ? `"${v.descrizione}": ${r.data.message}`
                : `"${v.descrizione}" è stato ripristinato.`);
            carica();
        } catch (e) {
            setErrore(messaggioErrore(e, 'Errore nel ripristino'));
        } finally {
            setInCorso(null);
        }
    };

    const confermaEliminazione = async () => {
        if (conferma !== 'ELIMINA') return;
        setErrore(null);
        setAvviso(null);
        try {
            setInCorso(daEliminare.id);
            await cestinoAPI.elimina(daEliminare.id, conferma);
            setAvviso('Eliminazione definitiva completata.');
            setDaEliminare(null);
            setConferma('');
            carica();
        } catch (e) {
            setErrore(messaggioErrore(e, 'Errore nell\'eliminazione'));
        } finally {
            setInCorso(null);
        }
    };

    /* Solo i conteggi: il riepilogo di una visita porta anche elenchi (i resi,
       gli appuntamenti) che servono al ripristino, non a chi guarda - e
       Number([12]) vale 12, quindi il filtro guarda il tipo. */
    const riepilogoTesto = (r) => {
        if (!r) return null;
        const parti = Object.entries(r)
            .filter(([k, n]) => typeof n === 'number' && n > 0 && k !== 'compito')
            .map(([k, n]) => `${n} ${k.replaceAll('_', ' ')}`);
        if (r.prodotti === 'usati') parti.push('prodotti usati: restano scaricati');
        if (r.prodotti === 'resi') parti.push('prodotti tornati in magazzino');
        /* 292: il «Riassegna» del compito li ha spostati sul paziente giusto */
        if (r.prodotti === 'riassegnati') parti.push('prodotti riassegnati al paziente giusto');
        return parti.length ? parti.join(', ') : 'nessun dato collegato';
    };

    return (
        <div className="ce-wrap">
            <div className="ce-testa">
                <div>
                    <h1>Cestino</h1>
                    <p className="ce-sottotitolo">
                        Dati cancellati e ancora recuperabili. Il ripristino riporta indietro
                        il record e tutto ciò che era stato nascosto insieme a lui.
                    </p>
                </div>
                <button className="ce-btn-secondario" onClick={() => carica(tipo, null)} disabled={caricamento}>
                    Aggiorna
                </button>
            </div>

            <div className="ce-filtro">
                <label htmlFor="ce-tipo">Mostra</label>
                <select id="ce-tipo" value={tipo} onChange={(e) => cambiaTipo(e.target.value)} disabled={caricamento}>
                    {FILTRI.map(([valore, nome]) => (
                        <option key={valore || 'tutto'} value={valore}>{nome} ({totale(valore)})</option>
                    ))}
                </select>
            </div>

            {errore && <div className="ce-errore">{errore}</div>}
            {avviso && <div className="ce-avviso">{avviso}</div>}

            {caricamento ? (
                <div className="ce-stato">Caricamento…</div>
            ) : voci.length === 0 ? (
                <div className="ce-vuoto">
                    <div className="ce-vuoto-icona"><Trash2 size={38} strokeWidth={1.2} aria-hidden="true" /></div>
                    <p>{tipo ? 'Nel cestino non ci sono voci di questo tipo.' : 'Il cestino è vuoto.'}</p>
                </div>
            ) : (
                <div className="rm-scorre">
                <table className="ce-tabella">
                    <thead>
                        <tr>
                            <th>Tipo</th>
                            <th>Descrizione</th>
                            <th>Dati collegati</th>
                            <th>Cancellato da</th>
                            <th>Quando</th>
                            <th>Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {voci.map((v) => (
                            <tr key={v.id}>
                                <td><span className="ce-tipo">{etichettaEntita[v.tipo] || etichettaEntita[v.entita] || v.entita}</span></td>
                                <td>
                                    <strong>{v.descrizione || '—'}</strong>
                                    {v.codice_fiscale && <div className="ce-cf">{v.codice_fiscale}</div>}
                                    {v.motivo && <div className="ce-motivo">{v.motivo}</div>}
                                </td>
                                <td className="ce-riepilogo">{riepilogoTesto(v.riepilogo) || '—'}</td>
                                <td>
                                    {v.utente_cognome
                                        ? `${v.utente_cognome} ${v.utente_nome || ''}`.trim()
                                        : <span className="ce-ignoto">non registrato</span>}
                                </td>
                                <td className="rm-intero">{dataOra(v.created_at)}</td>
                                <td>
                                    <div className="ce-azioni">
                                        <button
                                            className="ce-link"
                                            onClick={() => ripristina(v)}
                                            disabled={inCorso === v.id}
                                        >
                                            Ripristina
                                        </button>
                                        {v.entita === 'paziente' && (
                                            <button
                                                className="ce-link ce-link-pericolo"
                                                onClick={() => { setDaEliminare(v); setConferma(''); setErrore(null); }}
                                                disabled={inCorso === v.id}
                                            >
                                                Elimina
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            )}

            {!caricamento && altre && (
                <div className="ce-altre">
                    <span>Sono mostrate le {voci.length} voci più recenti.</span>
                    <button className="ce-btn-secondario" onClick={() => carica(tipo, voci[voci.length - 1])}
                            disabled={inPagina}>
                        {inPagina ? 'Caricamento…' : 'Mostra le più vecchie'}
                    </button>
                </div>
            )}

            {daEliminare && (
                <div className="ce-overlay" onClick={() => setDaEliminare(null)}>
                    <div className="ce-modale" onClick={(e) => e.stopPropagation()}>
                        <h2>Eliminazione definitiva</h2>
                        {/* IL TESTO DICE QUELLO CHE SUCCEDE, NON DI PIU'
                            (corretto il 23 settembre 2026). Diceva «Non sarà
                            più recuperabile in alcun modo», ed era falso: il
                            registro delle modifiche conserva `record_prima`
                            per sempre, di proposito - `pulisciRegistri.js`
                            dichiara in testa che modifiche_log non si tocca.
                            In produzione ci sono 47 righe DELETE di pazienti
                            che non esistono piu', tutte con codice fiscale,
                            nome, indirizzo, telefono ed email, piu' le righe
                            cancellate a cascata: documenti, consensi,
                            risposte ai questionari, firme grafiche.
                            La conservazione puo' essere giusta per gli
                            obblighi sulla cartella. Il difetto era che
                            l'operazione prometteva il contrario, e un
                            amministratore poteva dirlo in buona fede a un
                            paziente che aveva chiesto la cancellazione. */}
                        <div className="ce-allarme">
                            Stai per rimuovere <strong>{daEliminare.descrizione}</strong> e
                            tutti i dati collegati: visite, documenti firmati, foto, anamnesi.
                            I file verranno cancellati dal disco e la scheda non sarà più
                            recuperabile dall'applicazione.
                        </div>
                        <p className="ce-nota">
                            <strong>Quello che resta.</strong> Nel registro delle modifiche
                            rimangono la scheda e le righe cancellate così com'erano — anagrafica
                            completa, documenti, consensi, risposte ai questionari, firme — e
                            rimangono anche nelle copie di sicurezza notturne. È voluto: il
                            registro è la memoria di chi ha fatto cosa, e non si cancella.
                            <strong> Quindi non si può dire a un paziente che i suoi dati non
                            esistono più.</strong>
                        </p>
                        <p className="ce-nota">
                            Ricorda che sui dati sanitari esistono obblighi di conservazione che
                            possono prevalere sulla richiesta di cancellazione del paziente.
                        </p>
                        <div className="ce-campo">
                            <label htmlFor="conferma">
                                Per procedere, scrivi <code>ELIMINA</code>
                            </label>
                            <input
                                id="conferma"
                                type="text"
                                value={conferma}
                                onChange={(e) => setConferma(e.target.value)}
                                autoComplete="off"
                                placeholder="ELIMINA"
                            />
                        </div>
                        {errore && <div className="ce-errore">{errore}</div>}
                        <div className="ce-modale-azioni">
                            <button className="ce-btn-secondario" onClick={() => setDaEliminare(null)}>
                                Annulla
                            </button>
                            <button
                                className="ce-btn-pericolo"
                                onClick={confermaEliminazione}
                                disabled={conferma !== 'ELIMINA' || inCorso === daEliminare.id}
                            >
                                {inCorso === daEliminare.id ? 'Eliminazione…' : 'Elimina per sempre'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Cestino;
